import { errorToast } from "@/components/ui/toast.tsx";
import { api } from "@/lib/http.ts";
import type React from "react";
import { type ChangeEvent, useCallback, useRef, useState } from "react";
import { UI_CONSTANTS } from "./constants.ts";
import { isFileTypeSupported, partitionBySupport } from "./fileTypeSupport.ts";
import type { AttachedFile, SupportedTypesResponse } from "./types.ts";

/**
 * Above this many characters, a plain-text paste becomes a `.txt` attachment
 * instead of flooding the composer. Mirrors the large-paste-becomes-a-file
 * behavior in Claude.ai / ChatGPT (whose documented rule is ~5k chars); we sit
 * a little higher because attachments are ingested as an AI summary rather than
 * verbatim, so only genuinely huge pastes get diverted. Tune freely.
 */
const PASTE_AS_FILE_CHAR_THRESHOLD = 10_000;

/** Surface a concise toast naming the file(s) we couldn't attach. */
function toastRejectedFiles(rejected: File[]): void {
  if (rejected.length === 0) return;
  errorToast(
    rejected.length === 1
      ? `Can't attach "${rejected[0].name}" — unsupported file type`
      : `Can't attach ${rejected.length} files — unsupported file type`,
  );
}

export const useFileAttachment = (
  setAttachedFiles: (files: AttachedFile[] | ((prev: AttachedFile[]) => AttachedFile[])) => void,
) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [supportedTypes, setSupportedTypes] = useState<string>("");
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  // Holds the in-flight supported-types request so concurrent callers (e.g. a
  // paste while a drag is in progress) share one fetch instead of racing.
  const supportedTypesRequestRef = useRef<Promise<string> | null>(null);

  // Lazily resolve the server's accept list, caching it and falling back to a
  // basic set on failure. Shared by paste, drop, and the attach button.
  const ensureSupportedTypes = useCallback(async (): Promise<string> => {
    if (supportedTypes) return supportedTypes;
    if (!supportedTypesRequestRef.current) {
      supportedTypesRequestRef.current = (async () => {
        try {
          const response = (await api.get<SupportedTypesResponse>("/api/a/files/supported-types"))
            .data;
          setSupportedTypes(response.htmlAccept);
          return response.htmlAccept;
        } catch (error) {
          console.error("Failed to fetch supported file types:", error);
          setSupportedTypes(UI_CONSTANTS.DEFAULT_ACCEPT_TYPES);
          return UI_CONSTANTS.DEFAULT_ACCEPT_TYPES;
        } finally {
          supportedTypesRequestRef.current = null;
        }
      })();
    }
    return supportedTypesRequestRef.current;
  }, [supportedTypes]);

  // Append raw files as attachments, generating image previews asynchronously.
  // Shared entry point for the file picker, paste, and drag-and-drop.
  const addFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;

      const newAttachedFiles: AttachedFile[] = files.map((file) => {
        if (file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onload = (loadEvent) => {
            setAttachedFiles((prev) =>
              prev.map((af) =>
                af.file === file ? { ...af, preview: loadEvent.target?.result as string } : af,
              ),
            );
          };
          reader.readAsDataURL(file);
        }
        return { id: crypto.randomUUID(), file, uploading: false };
      });

      setAttachedFiles((prev) => [...prev, ...newAttachedFiles]);
    },
    [setAttachedFiles],
  );

  const handleAttachClick = useCallback(async () => {
    const accept = await ensureSupportedTypes();
    if (fileInputRef.current) {
      fileInputRef.current.accept = accept;
    }
    fileInputRef.current?.click();
  }, [ensureSupportedTypes]);

  const handleFileSelect = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      // The picker's `accept` only *hints* the OS dialog — users can still force-
      // select a non-matching file on some platforms — so validate here too and
      // toast anything unsupported rather than letting it 400 at send.
      const acceptTypes = supportedTypes || UI_CONSTANTS.DEFAULT_ACCEPT_TYPES;
      const { supported, rejected } = partitionBySupport(
        Array.from(e.target.files || []),
        acceptTypes,
      );
      addFiles(supported);
      toastRejectedFiles(rejected);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [addFiles, supportedTypes],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const clipboardData = e.clipboardData;
      if (!clipboardData) return;

      // Decide synchronously: the DataTransferItemList is only valid during the
      // event, and preventDefault must run before any await to take effect — so
      // we filter against the cached accept list (or the default set until the
      // server list loads) and only suppress the default when we actually have
      // something to attach. Otherwise an unsupported file would swallow a paste
      // that the textarea should have received verbatim. Warm the cache for next
      // time regardless.
      const acceptTypes = supportedTypes || UI_CONSTANTS.DEFAULT_ACCEPT_TYPES;
      void ensureSupportedTypes();

      const pastedFiles: File[] = [];
      for (const item of clipboardData.items) {
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file) pastedFiles.push(file);
        }
      }

      if (pastedFiles.length > 0) {
        const { supported, rejected } = partitionBySupport(pastedFiles, acceptTypes);
        // Supported files own the event: attach them and toast any rejects.
        if (supported.length > 0) {
          e.preventDefault();
          addFiles(supported);
          toastRejectedFiles(rejected);
          return;
        }
        // Only unsupported files. Suppress the (no-op) default and explain the
        // rejection ONLY when there's no co-present text — otherwise fall
        // through so the text still pastes normally (e.g. copying a web element
        // that carries both an unsupported image blob and text), matching the
        // pre-change behavior of never hijacking a text paste.
        if (clipboardData.getData("text/plain").length === 0) {
          e.preventDefault();
          toastRejectedFiles(rejected);
          return;
        }
      }

      // No attachable file on the clipboard: divert a very large text paste into
      // a `.txt` attachment instead of flooding the composer. Smaller pastes —
      // or a text type the backend doesn't accept — fall through to the
      // textarea's normal verbatim paste.
      const text = clipboardData.getData("text/plain");
      if (text.length > PASTE_AS_FILE_CHAR_THRESHOLD) {
        const textFile = new File([text], "pasted-text.txt", { type: "text/plain" });
        if (isFileTypeSupported(textFile, acceptTypes)) {
          e.preventDefault();
          addFiles([textFile]);
        }
      }
    },
    [supportedTypes, ensureSupportedTypes, addFiles],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    // Only react to file drags; ignore text/element drags so we don't hijack them.
    if (!e.dataTransfer.types.includes("Files")) return;
    // Preventing default on dragover is required for the drop event to fire.
    e.preventDefault();
    setIsDraggingOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    // dragleave fires when crossing into child elements too; only clear the
    // overlay once the pointer actually exits the drop zone.
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDraggingOver(false);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      if (!e.dataTransfer.types.includes("Files")) return;
      e.preventDefault();
      setIsDraggingOver(false);

      // Snapshot the FileList synchronously before the await releases the event.
      const droppedFiles = Array.from(e.dataTransfer.files);
      const acceptTypes = await ensureSupportedTypes();
      const { supported, rejected } = partitionBySupport(droppedFiles, acceptTypes);
      addFiles(supported);
      toastRejectedFiles(rejected);
    },
    [ensureSupportedTypes, addFiles],
  );

  return {
    fileInputRef,
    supportedTypes,
    isDraggingOver,
    handleAttachClick,
    handleFileSelect,
    handlePaste,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  };
};
